/**
 * stompWebSocket — 브라우저 native WebSocket 위에서 STOMP 1.2 프레임을
 * 직접 구현한 경량 클라이언트.
 *
 * 선택 이유:
 *   - @stomp/stompjs 가 apps/web/package.json 에 없음.
 *   - 새 의존성 추가 금지 규칙 (CRITICAL CONSTRAINT).
 *   - STOMP 1.2 텍스트 프레임은 단순하므로 native WebSocket + 수동 파싱으로 충분.
 *
 * STOMP 1.2 프레임 구조:
 *   COMMAND\n
 *   header1:value1\n
 *   header2:value2\n
 *   \n
 *   body\0
 *
 * 지원 프레임: CONNECT, CONNECTED, SUBSCRIBE, SEND, MESSAGE, ERROR, DISCONNECT, HEARTBEAT
 */

import { getStoredToken } from '../../login/api/loginApi';

export type StompCommand =
  | 'CONNECT'
  | 'CONNECTED'
  | 'SUBSCRIBE'
  | 'UNSUBSCRIBE'
  | 'SEND'
  | 'MESSAGE'
  | 'ERROR'
  | 'DISCONNECT'
  | 'RECEIPT';

export interface StompFrame {
  command: StompCommand;
  headers: Record<string, string>;
  body: string;
}

export interface StompSubscription {
  id: string;
  destination: string;
  unsubscribe: () => void;
}

export type StompMessageCallback = (frame: StompFrame) => void;
export type StompErrorCallback = (frame: StompFrame | null, ev?: Event) => void;
export type StompConnectCallback = () => void;
export type StompDisconnectCallback = () => void;

export interface StompClientOptions {
  /** WS URL, e.g. wss://host/api/eraf/ws */
  brokerURL: string;
  /** 재연결 대기(ms), 0 = 재연결 안 함 */
  reconnectDelay?: number;
  /** STOMP heartbeat [outgoing ms, incoming ms] */
  heartbeatOutgoing?: number;
  heartbeatIncoming?: number;
  onConnect?: StompConnectCallback;
  onDisconnect?: StompDisconnectCallback;
  onStompError?: StompErrorCallback;
  onWebSocketError?: StompErrorCallback;
}

/** NULL byte — STOMP 프레임 종료 */
const NULL_BYTE = '\x00';

/** STOMP 프레임 직렬화 */
function serializeFrame(frame: Omit<StompFrame, 'command'> & { command: string }): string {
  const headerStr = Object.entries(frame.headers)
    .map(([k, v]) => `${k}:${v}`)
    .join('\n');
  return `${frame.command}\n${headerStr}\n\n${frame.body ?? ''}${NULL_BYTE}`;
}

/** STOMP 프레임 파싱 (단일 프레임, heartbeat 포함) */
function parseFrame(raw: string): StompFrame | null {
  // heartbeat — 빈 문자열 또는 개행만
  if (!raw || raw.trim() === '') return null;

  const nullIdx = raw.indexOf(NULL_BYTE);
  const frameStr = nullIdx >= 0 ? raw.substring(0, nullIdx) : raw;
  const lines = frameStr.split('\n');

  if (lines.length === 0) return null;

  const command = lines[0].trim() as StompCommand;
  if (!command) return null;

  const headers: Record<string, string> = {};
  let bodyStart = 1;

  for (let i = 1; i < lines.length; i++) {
    const line = lines[i];
    if (line === '') {
      bodyStart = i + 1;
      break;
    }
    const colonIdx = line.indexOf(':');
    if (colonIdx > 0) {
      const key = line.substring(0, colonIdx).trim();
      const value = line.substring(colonIdx + 1).trim();
      headers[key] = value;
    }
  }

  // STOMP 프레임은 NUL(\x00) 로 끝난다 — 제어문자 정규식이 규격상 필수.
  // eslint-disable-next-line no-control-regex
  const body = lines.slice(bodyStart).join('\n').replace(/\x00$/, '');
  return { command, headers, body };
}

let _subIdCounter = 0;
function nextSubId(): string {
  return `sub-${++_subIdCounter}`;
}

/**
 * StompWebSocketClient — native WebSocket 기반 STOMP 1.2 클라이언트
 *
 * 사용 패턴:
 *   const client = new StompWebSocketClient({ brokerURL, reconnectDelay: 5000 });
 *   client.onConnect = () => { client.subscribe('/topic/console.lobby', cb); };
 *   client.activate();
 *   // ...
 *   client.deactivate();
 */
export class StompWebSocketClient {
  private options: StompClientOptions;
  private ws: WebSocket | null = null;
  private subscriptions: Map<string, StompMessageCallback> = new Map();
  private subscriptionDests: Map<string, string> = new Map();
  private active = false;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;

  onConnect?: StompConnectCallback;
  onDisconnect?: StompDisconnectCallback;
  onStompError?: StompErrorCallback;
  onWebSocketError?: StompErrorCallback;

  constructor(options: StompClientOptions) {
    this.options = options;
    this.onConnect = options.onConnect;
    this.onDisconnect = options.onDisconnect;
    this.onStompError = options.onStompError;
    this.onWebSocketError = options.onWebSocketError;
  }

  get connected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  /** WebSocket 연결 시작 */
  activate(): void {
    this.active = true;
    this._connect();
  }

  /** 연결 해제 + 재연결 중단 */
  deactivate(): void {
    this.active = false;
    this._clearReconnect();
    this._clearHeartbeat();
    if (this.ws) {
      try {
        if (this.ws.readyState === WebSocket.OPEN) {
          this.ws.send(serializeFrame({ command: 'DISCONNECT', headers: {}, body: '' }));
        }
        this.ws.close();
      } catch {
        // ignore close errors
      }
      this.ws = null;
    }
  }

  /**
   * STOMP SUBSCRIBE — destination 구독
   * @returns StompSubscription (unsubscribe() 로 구독 해제)
   */
  subscribe(destination: string, callback: StompMessageCallback): StompSubscription {
    const id = nextSubId();
    this.subscriptions.set(id, callback);
    this.subscriptionDests.set(id, destination);

    if (this.connected && this.ws) {
      this.ws.send(
        serializeFrame({
          command: 'SUBSCRIBE',
          headers: { id, destination },
          body: '',
        })
      );
    }

    return {
      id,
      destination,
      unsubscribe: () => {
        this.subscriptions.delete(id);
        this.subscriptionDests.delete(id);
        if (this.connected && this.ws) {
          this.ws.send(
            serializeFrame({
              command: 'UNSUBSCRIBE',
              headers: { id },
              body: '',
            })
          );
        }
      },
    };
  }

  /**
   * STOMP SEND — destination 으로 메시지 발행
   */
  publish(params: { destination: string; body: string; headers?: Record<string, string> }): void {
    if (!this.connected || !this.ws) {
      console.warn('[StompWS] Cannot publish: not connected');
      return;
    }
    const headers: Record<string, string> = {
      destination: params.destination,
      'content-type': 'application/json',
      ...(params.headers ?? {}),
    };
    this.ws.send(serializeFrame({ command: 'SEND', headers, body: params.body }));
  }

  private _connect(): void {
    if (!this.active) return;
    this._clearReconnect();

    try {
      // ★ STOMP sub-protocol 협상 필수 — Spring SubProtocolWebSocketHandler 는
      //   Sec-WebSocket-Protocol(v12/v11.stomp) 가 없으면 프레임을 STOMP 핸들러로 라우팅하지 않아
      //   CONNECT 가 broker 에 도달하지 못함(연결 중 고착).
      this.ws = new WebSocket(this.options.brokerURL, ['v12.stomp', 'v11.stomp']);
    } catch (err) {
      console.error('[StompWS] WebSocket constructor error:', err);
      this._scheduleReconnect();
      return;
    }

    this.ws.onopen = () => {
      // STOMP CONNECT 프레임 전송 (Bearer 토큰 헤더)
      const token = getStoredToken();
      // ★ STOMP 표준 헤더명은 하이픈 — accept-version / heart-beat / host (언더스코어 X).
      const connectHeaders: Record<string, string> = {
        'accept-version': '1.2,1.1',
        'heart-beat': `${this.options.heartbeatOutgoing ?? 10000},${this.options.heartbeatIncoming ?? 10000}`,
        host: window.location.host,
      };
      if (token) {
        connectHeaders['Authorization'] = `Bearer ${token}`;
      }
      this.ws!.send(serializeFrame({ command: 'CONNECT', headers: connectHeaders, body: '' }));
    };

    this.ws.onmessage = (ev: MessageEvent) => {
      this._handleMessage(ev.data as string);
    };

    this.ws.onclose = () => {
      this._clearHeartbeat();
      this.onDisconnect?.();
      if (this.active) {
        this._scheduleReconnect();
      }
    };

    this.ws.onerror = (ev: Event) => {
      this.onWebSocketError?.(null, ev);
    };
  }

  private _handleMessage(raw: string): void {
    // 서버 heartbeat (개행만 오는 경우)
    if (!raw || raw.trim() === '' || raw === '\n') return;

    const frame = parseFrame(raw);
    if (!frame) return;

    switch (frame.command) {
      case 'CONNECTED':
        this._startHeartbeat();
        // 이전 구독 복원 (재연결 후)
        this._resubscribeAll();
        this.onConnect?.();
        break;

      case 'MESSAGE': {
        const subId = frame.headers['subscription'];
        if (subId) {
          const cb = this.subscriptions.get(subId);
          cb?.(frame);
        }
        break;
      }

      case 'ERROR':
        this.onStompError?.(frame);
        break;

      default:
        break;
    }
  }

  private _resubscribeAll(): void {
    if (!this.connected || !this.ws) return;
    this.subscriptions.forEach((_, id) => {
      const dest = this.subscriptionDests.get(id);
      if (dest) {
        this.ws!.send(
          serializeFrame({
            command: 'SUBSCRIBE',
            headers: { id, destination: dest },
            body: '',
          })
        );
      }
    });
  }

  private _scheduleReconnect(): void {
    const delay = this.options.reconnectDelay ?? 5000;
    if (!this.active || delay <= 0) return;
    this.reconnectTimer = setTimeout(() => {
      if (this.active) this._connect();
    }, delay);
  }

  private _clearReconnect(): void {
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  private _startHeartbeat(): void {
    this._clearHeartbeat();
    const interval = this.options.heartbeatOutgoing ?? 10000;
    if (interval <= 0) return;
    this.heartbeatTimer = setInterval(() => {
      if (this.connected && this.ws) {
        this.ws.send('\n'); // STOMP heartbeat
      }
    }, interval);
  }

  private _clearHeartbeat(): void {
    if (this.heartbeatTimer !== null) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }
}

/**
 * STOMP WS URL 계산 — http(s) → ws(s) scheme 변환 + 환경변수 지원
 *
 * VITE_WS_BASE 가 설정되어 있으면 그대로 사용.
 * 없으면 VITE_API_BASE 에서 scheme 만 ws/wss 로 변환.
 * VITE_API_BASE 도 없으면 origin 기반 자동 계산.
 */
export function buildStompUrl(endpoint = '/api/eraf/ws'): string {
  const wsBase = import.meta.env.VITE_WS_BASE as string | undefined;
  if (wsBase) return `${wsBase}${endpoint}`;

  const apiBase = import.meta.env.VITE_API_BASE as string | undefined;
  if (apiBase) {
    // apiBase 가 상대경로이면 origin 으로 보완
    const resolved = apiBase.startsWith('http')
      ? apiBase
      : `${window.location.origin}${apiBase}`;
    const wsResolved = resolved.replace(/^http/, 'ws');
    return `${wsResolved}${endpoint}`;
  }

  // fallback: origin 기반
  const scheme = window.location.protocol === 'https:' ? 'wss' : 'ws';
  return `${scheme}://${window.location.host}${endpoint}`;
}
